import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import LeadForm from "@/components/LeadForm";

const QuoteFormContext = createContext<{ openQuoteForm: () => void }>({
  openQuoteForm: () => {},
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openQuoteForm = useCallback(() => setOpen(true), []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeQuoteForm(); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative w-full max-w-lg max-h-[calc(100dvh-32px)] overflow-y-auto scrollbar-slim rounded-2xl shadow-2xl bg-card"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between gap-4 bg-card/95 backdrop-blur border-b border-border/60 px-6 py-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary">
                    <Sparkles className="h-3.5 w-3.5" /> Free Estimate
                  </span>
                </div>
                <button
                  onClick={closeQuoteForm}
                  className="p-2 rounded-full bg-muted hover:bg-muted/80 text-foreground transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-3 sm:p-4">
                <LeadForm instanceId="popup" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </QuoteFormContext.Provider>
  );
};
